/**
 * V3 P6: the single item display rule for item-facing surfaces (DESIGN_V3 §5.5, C6).
 *
 * S2: registered restoration items take precedence using the S1b RPC's value/source and
 * qualification flag. V3 projector states remain the rule for unregistered items.
 *
 * An item's displayed value comes ONLY from that item's own stored state — never from its
 * catalogue variant's headline (`pricing_display_headline_used`) or history
 * (`variant_valuations`), and never from a shared instrument. This mirrors the SQL function
 * `public.item_valuation_state` (supabase/migrations/20260927b_v3_p6_item_valuation_readers.sql)
 * exactly; both are pinned by `__tests__/fixtures/itemValuationCases.json`.
 *
 * - `legacy`: a pre-V3 item (no V3 identity or valuation state). Its own stored valuation is
 *   shown, labelled — V3's documented temporary behaviour until the P8 projection covers it.
 * - `valued`: resolved identity and the projector marked it `valued_display` (P8).
 * - everything else shows NO value: identity unresolved / pending, valuation pending (every V3
 *   item until P8), or a projector status such as `condition_unknown`. A V3 item's
 *   legacy-written `valuation` is never shown through this path.
 *
 * `identity_status` here is `user_products.identity_status`, the mirror of the item's derived
 * identity (`item_identity_state`), which is unresolved for a conflicting user assertion even
 * when the raw identification row is resolved. Never infer identity from anything else.
 */

import { identityReasonLabel } from './itemIdentity';

export type ItemValuationState =
  | 'legacy'
  | 'unvalued'
  | 'valued'
  | 'identity_unresolved'
  | 'identity_pending'
  | 'pending'
  | 'condition_unknown'
  | 'condition_unsupported'
  | 'basis_unavailable'
  | 'basis_unqualified'
  | string;

export interface ItemValuationFields {
  valuation?: number | null;
  valuation_source?: string | null;
  restoration_valuation?: boolean;
  restoration_pending?: boolean;
  restoration_market_qualified?: boolean;
  image_analysis?: unknown;
  valuation_status?: string | null;
  valuation_basis?: string | null;
  identity_status?: string | null;
  identity_reason?: string | null;
}

export interface ItemValuationDisplay {
  state: ItemValuationState;
  /** The value to show, or null: never a fallback from catalogue or legacy state. */
  value: number | null;
  /** Short label shown next to (or instead of) the value. */
  label: string;
  /** True when the item shows no value and should count as unvalued. */
  unvalued: boolean;
}

export function itemValuationState(item: ItemValuationFields): ItemValuationState {
  if (item.restoration_valuation) {
    if (item.valuation_source === 'market_data' && item.restoration_market_qualified === true
      && item.valuation != null) return 'restoration_market';
    if (item.valuation_source === 'gemini_estimate' && item.valuation != null) return 'restoration_estimate';
    return item.restoration_pending ? 'pending' : 'unvalued';
  }
  const valuation = item.valuation ?? null;
  const valuationStatus = item.valuation_status ?? null;
  const identityStatus = item.identity_status ?? null;
  if (valuationStatus === null && identityStatus === null) {
    return valuation === null ? 'unvalued' : 'legacy';
  }
  if (identityStatus === 'unresolved') return 'identity_unresolved';
  if (identityStatus === 'pending') return 'identity_pending';
  if (valuationStatus === 'valued_display' && identityStatus === 'resolved' && valuation !== null) {
    return 'valued';
  }
  if (valuationStatus === null || valuationStatus === 'valued_display') return 'pending';
  return valuationStatus;
}

// V3 P8: every projected value is an ASKING-MARKET display value (what comparable items are
// listed for). Labels must never imply a realised, clearing or guaranteed sale price.
const BASIS_LABELS: Record<string, string> = {
  verified_median: 'Verified asking price',
  trusted_anchor: 'Asking price',
  market_median: 'Asking price (market median)',
};

const IDENTITY_REASON_LABELS: Record<string, string> = {
  user_assertion_conflict: 'Selected variant conflicts with the identified item',
  stale_legacy_write: 'Identification needs re-checking',
};

const STATE_LABELS: Record<string, string> = {
  unvalued: 'Not valued',
  identity_unresolved: 'Needs identification',
  identity_pending: 'Identification pending',
  pending: 'Valuation pending',
  condition_unknown: 'Condition needed',
  condition_unsupported: 'No price for this condition',
  basis_unavailable: 'No market price yet',
  basis_unqualified: 'No market price yet',
};

export function itemValuationDisplay(item: ItemValuationFields): ItemValuationDisplay {
  const state = itemValuationState(item);
  if (state === 'restoration_market' || state === 'restoration_estimate') {
    return { state, value: item.valuation ?? null,
      label: state === 'restoration_market' ? 'Qualified market asking price' : 'Cache AI estimate', unvalued: false };
  }
  if (state === 'legacy') {
    return { state, value: item.valuation ?? null, label: 'Estimate', unvalued: false };
  }
  if (state === 'valued') {
    return {
      state,
      value: item.valuation ?? null,
      label: BASIS_LABELS[item.valuation_basis ?? ''] ?? 'Asking price',
      unvalued: false,
    };
  }
  // T1: an item the scan identity resolver saved unresolved carries a namespaced reason
  // (scan-v1:resolve|tracked|tracked_exact|unavailable:...) with its own owner-approved copy.
  const reason =
    state === 'identity_unresolved'
      ? (IDENTITY_REASON_LABELS[item.identity_reason ?? ''] ?? identityReasonLabel(item.identity_reason) ?? undefined)
      : undefined;
  return {
    state,
    value: null,
    label: reason ?? STATE_LABELS[state] ?? 'Not valued',
    unvalued: true,
  };
}

/** The displayed value, or null. The only value item-facing totals may add up. */
export function itemDisplayValue(item: ItemValuationFields): number | null {
  return itemValuationDisplay(item).value;
}

export interface ItemValuationTotals {
  total: number;
  valuedCount: number;
  unvaluedCount: number;
}

/** Portfolio-style totals over items: only displayed values; everything else is counted, not guessed. */
export function itemValuationTotals(items: ItemValuationFields[]): ItemValuationTotals {
  let total = 0;
  let valuedCount = 0;
  let unvaluedCount = 0;
  for (const item of items) {
    const value = itemDisplayValue(item);
    if (value === null) {
      unvaluedCount += 1;
    } else {
      total += value;
      valuedCount += 1;
    }
  }
  return { total, valuedCount, unvaluedCount };
}
